import React from 'react'
import { createRoot, type Root } from 'react-dom/client'
import PrintableReport from '../components/print/PrintableReport'
import type { PrintConfig } from '../types/print'

const PRINT_HOST_ID = 'system-print-host'
const CLEANUP_FALLBACK_MS = 10_000

const ensurePrintHost = (): HTMLDivElement => {
  const existing = document.getElementById(PRINT_HOST_ID) as HTMLDivElement | null
  if (existing) return existing
  const host = document.createElement('div')
  host.id = PRINT_HOST_ID
  document.body.appendChild(host)
  return host
}

const removePrintHost = () => {
  const host = document.getElementById(PRINT_HOST_ID)
  if (host?.parentNode) host.parentNode.removeChild(host)
}

const getPrintStyleSheet = (): string => `
  @media print {
    body > *:not(#${PRINT_HOST_ID}) {
      display: none !important;
    }
    #${PRINT_HOST_ID} {
      display: block !important;
      position: static;
      width: 100%;
      padding: 16px;
      color: #111827;
      background: #ffffff;
      font-size: 12px;
      overflow: visible;
    }
    #${PRINT_HOST_ID} .print-table {
      width: 100%;
      border-collapse: collapse;
    }
    #${PRINT_HOST_ID} .print-table thead {
      display: table-header-group;
    }
    #${PRINT_HOST_ID} .print-table tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    #${PRINT_HOST_ID} .print-table th,
    #${PRINT_HOST_ID} .print-table td {
      border: 1px solid #d1d5db;
      padding: 6px 8px;
      vertical-align: top;
    }
    #${PRINT_HOST_ID} .print-meta {
      margin-bottom: 8px;
    }
    #${PRINT_HOST_ID} .print-filters {
      margin-bottom: 12px;
    }
    #${PRINT_HOST_ID} .print-empty {
      padding: 12px;
      border: 1px solid #d1d5db;
    }
  }
`

const print = (config: PrintConfig): void => {
  const host = ensurePrintHost()
  let root: Root | null = createRoot(host)

  const cleanup = () => {
    window.removeEventListener('afterprint', cleanup)
    if (root) {
      root.unmount()
      root = null
    }
    removePrintHost()
  }

  window.addEventListener('afterprint', cleanup, { once: true })

  root.render(
    React.createElement(
      React.Fragment,
      null,
      React.createElement('style', null, getPrintStyleSheet()),
      React.createElement(PrintableReport, config),
    ),
  )

  window.setTimeout(() => {
    window.print()
  }, 0)

  window.setTimeout(cleanup, CLEANUP_FALLBACK_MS)
}

export const printService = {
  print,
}

